import { useNavigate, useLocation } from 'react-router-dom';
import { useRef, useCallback } from 'react';

/**
 * useThemeNavigate Hook
 * 
 * A custom hook that extends the useNavigate hook to preserve URL parameters,
 * especially the theme parameter, during programmatic navigation.
 * 
 * @returns {Function} A navigate function that preserves parameters
 */
const useThemeNavigate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lastNavigateRef = useRef(null);
  
  /**
   * Navigate while preserving URL parameters
   * 
   * @param {string|object} to - Target path or location object
   * @param {object} options - Navigation options
   */
  const themeNavigate = useCallback((to, options = {}) => {
    // Get current URL search params
    const currentParams = new URLSearchParams(location.search);
    
    // If this is the same path we just navigated to, do nothing
    // This helps prevent navigation loops
    if (typeof to === 'string' && lastNavigateRef.current === to) {
      return;
    }
    
    // Store the target for future reference
    if (typeof to === 'string') {
      lastNavigateRef.current = to;
    } else if (to && to.pathname) {
      lastNavigateRef.current = to.pathname;
    }
    
    // Preserve all current query params during navigation
    if (currentParams.toString()) {
      // Handle location object
      if (typeof to === 'object' && to.pathname) {
        const newSearchParams = new URLSearchParams(to.search || '');
        
        // Carry over any current params not already set in the target
        for (const [key, val] of currentParams) {
          if (!newSearchParams.has(key)) {
            newSearchParams.set(key, val);
          }
        }
        
        navigate({
          ...to,
          search: newSearchParams.toString()
        }, options);
        
        return;
      }
      
      // Handle string path
      const hasParams = to.includes('?');
      
      if (hasParams) {
        // Parse target path's query params
        const [path, search] = to.split('?');
        const targetParams = new URLSearchParams(search);
        
        // Carry over any current params not already set in the target
        for (const [key, val] of currentParams) {
          if (!targetParams.has(key)) {
            targetParams.set(key, val);
          }
        }
        
        navigate(`${path}?${targetParams.toString()}`, options);
      } else {
        // No params in target path — carry over all current params
        navigate(`${to}?${currentParams.toString()}`, options);
      }
    } else {
      // No current params to preserve, use regular navigation
      navigate(to, options);
    }
  }, [navigate, location.search]);
  
  return themeNavigate;
};

export default useThemeNavigate; 